/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useRef } from 'react';
import { Panel } from 'primereact/panel';
import {
  CWLViewer,
  elementToFullscreen,
  extractCWLGraphFromRaw,
  ProcessDescription,
  useProcessesContext
} from 'ogc-processes-components';
import { Button } from 'primereact/button';
import { Expand } from 'lucide-react';
import { ProcessCarboneScore } from '@/components/ProcessesWrapper/ProcessCarboneScore.tsx';
import { useQuery } from '@tanstack/react-query';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { Project } from '@/types/models.ts';
import { Message } from 'primereact/message';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';

interface Props {
  process: ProcessDescription;
  project: Project;
}
export const ProcessCWLDescription: React.FC<Props> = ({ process, project }) => {
  const fullScreenRef = useRef(null);
  const { processesEndpoint } = useProcessesContext();
  const { t } = useTranslation();

  const { data: workflowModel, isLoading: loadingWorkflowModel } = useQuery({
    queryKey: ['get_zero_process_package', processesEndpoint, process, project],
    queryFn: async () => {
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      const { path, ref } = project;
      const processPackage = await endpoint.get_project_process_package(path, ref, process.id);
      return extractCWLGraphFromRaw({ raw: processPackage.content, mainWorkflowId: processPackage.main });
    },
    refetchOnWindowFocus: false
  });

  return (
    <div className="sm:col-12 md:col-12 lg:col-7 xl:col-7">
      <Panel
        headerTemplate={
          <div className="flex justify-content-between w-full align-items-center surface-50 surface-border px-4 py-3 border-1 border-round-top">
            <div className="flex align-items-center">
              <span className="inline-block font-bold">{process.title}</span>
              <ProcessCarboneScore className="ml-4" help process={process} />
            </div>
            {workflowModel && (
              <div className="cursor-pointer">
                <Button
                  id="refresh-cwl-viewer"
                  severity="contrast"
                  className="mx-2"
                  style={{ height: '30px' }}
                  icon="pi pi-refresh"
                />
                <Button
                  onClick={() => {
                    if (fullScreenRef.current) {
                      elementToFullscreen(fullScreenRef.current);
                    }
                  }}
                  severity="contrast"
                  style={{ height: '30px' }}
                  icon={<Expand size={18} />}
                />
              </div>
            )}
          </div>
        }
        className="mb-2"
      >
        {loadingWorkflowModel ? (
          <div style={{ height: '35rem' }}>
            <Skeleton className="h-full w-full" />
          </div>
        ) : workflowModel ? (
          <div style={{ height: '35rem' }}>
            <CWLViewer
              outerRefresh="refresh-cwl-viewer"
              ref={fullScreenRef}
              className="h-full"
              workflow={workflowModel}
              loading={loadingWorkflowModel}
            />
          </div>
        ) : (
          <Message severity="warn" text={t(['misc.no_inputs_provided', 'no inputs'])} className={'w-full h-full'} />
        )}
      </Panel>
    </div>
  );
};
